import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import QRCode from 'qrcode';
import { translations, translateMedicationInstruction } from './translations';
import { getClinicWebsiteBase } from '../config/siteUrls';
import {
    isReportSectionIncluded,
    REPORT_SECTION_INCLUSION_KEYS,
    translateReportDocumentTitle,
} from './reportLabels';
import { formatFindingPairForDisplay } from './ophthalmicFindings';
import { addPhysicianSignatureToPdf } from './pdfSignature';
import { writePdfLabeledFreeText } from './pdfArabicFont';

const PDF_PAGE_FORMAT = 'a5';
/** A5 portrait: ~1cm from the physical left edge (content starts here). */
const PRINT_MARGIN_LEFT = 10;
/** ~3cm right gutter (binding / no-print zone); keeps right-side QR inside the printable strip. */
const PRINT_MARGIN_RIGHT = 30;
const PRINT_MARGIN = { left: PRINT_MARGIN_LEFT, right: PRINT_MARGIN_RIGHT };
const COMPACT_CELL_PADDING = { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 };
/** Header/body top inset: header band starts 40mm below page top. */
const PDF_PAGE_TOP_MARGIN_MM = 40;
/** Extra downward shift for footer chrome only (tel/address), not header or pdfPageY. */
const PDF_FOOTER_EXTRA_OFFSET_MM = 10;
const pdfPageY = (y) => y + PDF_PAGE_TOP_MARGIN_MM;

const createPdfDoc = () => new jsPDF({ unit: 'mm', format: PDF_PAGE_FORMAT, orientation: 'portrait' });
const getContentWidth = (pageWidth) => pageWidth - PRINT_MARGIN_LEFT - PRINT_MARGIN_RIGHT;
const getContentCenterX = (pageWidth) => PRINT_MARGIN_LEFT + getContentWidth(pageWidth) / 2;
const compactTableStyles = (fontSize = 8) => ({
    fontSize,
    cellPadding: COMPACT_CELL_PADDING,
    overflow: 'ellipsize',
    minCellHeight: fontSize <= 8 ? 4.6 : 5.4,
});
const compactHeadStyles = (fontSize = 8) => ({
    fillColor: [0, 128, 128],
    textColor: 255,
    fontStyle: 'bold',
    fontSize,
    cellPadding: COMPACT_CELL_PADDING,
    overflow: 'ellipsize',
});
const neutralMedicationHeadStyles = (fontSize = 8) => ({
    fillColor: [229, 231, 235],
    textColor: [17, 24, 39],
    fontStyle: 'bold',
    fontSize,
    cellPadding: COMPACT_CELL_PADDING,
    overflow: 'ellipsize',
});

/** Header-band QR pair (Facebook left, portal right) beside patient / date / reference lines. */
export const PDF_QR_SIZE_MM = 11;
const PDF_HEADER_QR_PAIR_GAP_MM = 2;
const PDF_HEADER_QR_TEXT_GAP_MM = 3;
const PDF_HEADER_QR_BELOW_MM = 1;
const PDF_FOOTER_TEL_OFFSET_MM = 21;
const PDF_FOOTER_ADDR_OFFSET_MM = 14;
const PDF_CONTENT_CLEARANCE_ABOVE_FOOTER_MM = 6;

const getPdfBottomLayout = (pageHeight) => {
    const footerYOffset = PDF_PAGE_TOP_MARGIN_MM + PDF_FOOTER_EXTRA_OFFSET_MM;
    const footerTelY = pageHeight - PDF_FOOTER_TEL_OFFSET_MM + footerYOffset;
    const footerAddrY = pageHeight - PDF_FOOTER_ADDR_OFFSET_MM + footerYOffset;
    const contentMaxY = footerTelY - PDF_CONTENT_CLEARANCE_ABOVE_FOOTER_MM;
    return {
        footerTelY,
        footerAddrY,
        contentMaxY,
        bottomMargin: pageHeight - contentMaxY,
    };
};

const getPdfTableMargins = (doc) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    const { bottomMargin } = getPdfBottomLayout(pageHeight);
    return { left: PRINT_MARGIN_LEFT, right: PRINT_MARGIN_RIGHT, bottom: bottomMargin };
};

const ensurePdfVerticalSpace = (doc, pageWidth, isArabic, currentY, neededMm) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    const { contentMaxY } = getPdfBottomLayout(pageHeight);
    if (currentY + neededMm <= contentMaxY) return currentY;
    doc.addPage(PDF_PAGE_FORMAT, 'portrait');
    addHeader(doc, pageWidth, isArabic);
    return pdfPageY(45);
};

const stampFixedPageChrome = (doc, pageWidth, isArabic = false) => {
    const total = doc.internal.getNumberOfPages();
    for (let page = 1; page <= total; page += 1) {
        doc.setPage(page);
        addFooter(doc, pageWidth, isArabic);
    }
};

const finalizePatientReportPdf = async (doc, pageWidth, isArabic, patientId, contentEndY) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    const { contentMaxY } = getPdfBottomLayout(pageHeight);
    let sigY = contentEndY ?? pdfPageY(80);
    if (sigY > contentMaxY - 18) {
        doc.addPage(PDF_PAGE_FORMAT, 'portrait');
        addHeader(doc, pageWidth, isArabic);
        sigY = pdfPageY(45);
    }
    await addPhysicianSignatureToPdf(doc, pageWidth, sigY, {
        isArabic,
        maxY: contentMaxY,
    });
    await stampFixedPageChrome(doc, pageWidth, isArabic, patientId);
};

/**
 * Scale medication prescription table typography / padding by row count so
 * typical multi-med lists stay on one A5 portrait page with header + patient (incl. QRs) + steroid regimen + footer.
 * Thresholds: default ≤4, then progressively denser above 4, 6, 8, 10, and 14 rows.
 */
const getMedicationTableLayout = (rowCount, contentWidth) => {
    let bodyFont = 8;
    let headFont = 11;
    let cellPadding = COMPACT_CELL_PADDING;
    let minCellHeight = 4.6;
    if (rowCount > 14) {
        bodyFont = 5;
        headFont = 6;
        cellPadding = { top: 0.65, right: 0.75, bottom: 0.65, left: 0.75 };
        minCellHeight = 2.8;
    } else if (rowCount > 10) {
        bodyFont = 5.5;
        headFont = 6.5;
        cellPadding = { top: 0.8, right: 0.85, bottom: 0.8, left: 0.85 };
        minCellHeight = 3.05;
    } else if (rowCount > 8) {
        bodyFont = 6;
        headFont = 7;
        cellPadding = { top: 1, right: 1, bottom: 1, left: 1 };
        minCellHeight = 3.4;
    } else if (rowCount > 6) {
        bodyFont = 6.5;
        headFont = 8;
        cellPadding = { top: 1.15, right: 1.15, bottom: 1.15, left: 1.15 };
        minCellHeight = 3.7;
    } else if (rowCount > 4) {
        bodyFont = 7;
        headFont = 9;
        cellPadding = { top: 1.25, right: 1.25, bottom: 1.25, left: 1.25 };
        minCellHeight = 4.1;
    }
    const w0 = Math.round(contentWidth * 0.062);
    const w3 = Math.round(contentWidth * 0.152);
    const w2 = Math.round(contentWidth * 0.275);
    const w1 = Math.max(22, contentWidth - w0 - w2 - w3);
    return {
        bodyFont,
        headFont,
        cellPadding,
        minCellHeight,
        columnWidths: [w0, w1, w2, w3],
    };
};

// Clinic Header Info
const CLINIC_INFO = {
    name: 'Dr. Mahmoud Sami Abouzeid',
    nameAr: 'د. محمود سامي أبوزيد',
    title1: 'Consultant Ophthalmologist',
    title1Ar: 'استشاري طب وجراحة العيون',
    title2: 'Fellow of Royal College of Physicians & Surgeons of Glasgow, UK',
    title2Ar: 'زميل الكلية الملكية للجراحين',
    title3: 'Fellow of International Council of Ophthalmology, Switzerland',
    title3Ar: 'زميل المجلس الدولي لطب العيون',
    clinic: 'Eye Care Clinic',
    clinicAr: 'عيادة العيون',
    address: '1 Zaki Nabawi - Alkawthar Tower - Talbieh, Haram',
    addressAr: '١ ش زكي نبوي - برج الكوثر - طالبية الهرم',
    phone: '01005602267',
    email: 'contact@eyecareclinic.com',
    facebook: 'https://genododi.github.io/drmahmoud/fb.html'
};

// Pre-generated QR data URLs cache (keyed by URL). Promises resolve to data URLs
// or null on error. We eagerly start generating the clinic-website QR so it is
// almost always ready by the time a user clicks Print.
const _qrCache = new Map();
const getQRDataURL = (url, options = {}) => {
    const key = `${url}::${options.dark || ''}`;
    if (!_qrCache.has(key)) {
        _qrCache.set(
            key,
            QRCode.toDataURL(url, {
                width: 200,
                margin: 1,
                color: { dark: options.dark || '#0e7490', light: '#ffffff' }
            }).catch((err) => {
                console.error('QR generation error:', err);
                return null;
            })
        );
    }
    return _qrCache.get(key);
};
// Warm caches: clinic-website QR (teal) and Facebook QR (Facebook blue)
getQRDataURL(getClinicWebsiteBase());
getQRDataURL(CLINIC_INFO.facebook, { dark: '#1877F2' });

/**
 * Build the URL that a patient can scan/visit on the clinic website to access
 * their personal records. If a patient id is given, deep-links into the portal.
 */
const buildPatientPortalURL = (patientId) => {
    const base = getClinicWebsiteBase().replace(/\/$/, '');
    return patientId ? `${base}/portal.html#id=${encodeURIComponent(patientId)}` : `${base}/`;
};

/** Render one QR image anchored at (x, y) with the given size (no captions). */
const _drawQRImage = (doc, dataURL, x, y, size) => {
    try {
        doc.addImage(dataURL, 'PNG', x, y, size, size);
    } catch (e) {
        console.error('addImage QR error', e);
    }
};

/** Layout for Facebook + portal QRs to the right of the patient / date / reference block. */
const getPatientHeaderQrLayout = (pageWidth, blockTopY) => {
    const size = PDF_QR_SIZE_MM;
    const pairGap = PDF_HEADER_QR_PAIR_GAP_MM;
    const xPortal = pageWidth - PRINT_MARGIN_RIGHT - size;
    const xFb = xPortal - pairGap - size;
    const y = blockTopY;
    const qrBandWidth = size * 2 + pairGap;
    const textMaxWidth = Math.max(40, getContentWidth(pageWidth) - qrBandWidth - PDF_HEADER_QR_TEXT_GAP_MM);
    const blockBottomY = y + size + PDF_HEADER_QR_BELOW_MM;
    return { size, xFb, xPortal, y, textMaxWidth, blockBottomY };
};

/**
 * Facebook (left) + patient portal (right) QRs in the patient-header vertical band.
 * Left QR edge stays at or right of PRINT_MARGIN_LEFT; right QR stays inside the printable strip.
 */
const addPatientHeaderQRs = async (doc, layout, patientId = null) => {
    const { size, xFb, xPortal, y } = layout;

    const fbDataURL = await getQRDataURL(CLINIC_INFO.facebook, { dark: '#1877F2' });
    if (fbDataURL) {
        _drawQRImage(doc, fbDataURL, xFb, y, size);
    }

    const websiteUrl = buildPatientPortalURL(patientId);
    const websiteDataURL = await getQRDataURL(websiteUrl);
    if (websiteDataURL) {
        _drawQRImage(doc, websiteDataURL, xPortal, y, size);
    }
};

const getPatientId = (patient) => {
    if (!patient || typeof patient !== 'object') return null;
    const v = patient.referenceId ?? patient.patientId ?? patient.patient_id ?? patient.patientID ?? patient.id;
    return v != null && String(v).trim() !== '' ? String(v).trim() : null;
};

/** Portal / QR id when `patient` is a string or object without id — from explicit print options. */
const resolvePortalPatientId = (patient, options = {}) => {
    const fromObj = getPatientId(patient);
    if (fromObj) return fromObj;
    const opt = options.patientId ?? options.referenceId;
    return opt != null && String(opt).trim() !== '' ? String(opt).trim() : null;
};

/** Reference / file id from a clinical, lab, or billing row when callers only pass patient name string. */
const patientIdFromRecord = (record) => {
    if (!record || typeof record !== 'object') return null;
    const v = record.referenceId ?? record.patientId ?? record.patient_id ?? record.patientID;
    return v != null && String(v).trim() !== '' ? String(v).trim() : null;
};

/** Merge explicit PDF options with id stored on the source record (examination, glasses, etc.). */
const resolvePatientFileIdForPdf = (options = {}, record = null) => {
    const { patientId = null, referenceId = null } = options;
    return patientId ?? referenceId ?? patientIdFromRecord(record) ?? null;
};

/** Legacy `print*(…, isArabic)` boolean third argument, or an options object with `isArabic`, `patientId`, etc. */
const mergeArabicOrPdfOptions = (arg) => {
    if (arg === true || arg === false) return { isArabic: arg };
    if (arg != null && typeof arg === 'object' && !Array.isArray(arg)) return { isArabic: false, ...arg };
    return { isArabic: false };
};

const mergeExamPdfOptions = (arg) => {
    if (arg != null && typeof arg === 'object' && !Array.isArray(arg)) return { ...arg };
    return {};
};

const printPdfDoc = (doc, fileName = 'document.pdf') => {
    if (typeof document === 'undefined' || typeof URL === 'undefined') {
        doc.output('dataurlnewwindow');
        return fileName;
    }

    // Open the PDF directly. Calling window.print() from an async PDF render path
    // can trigger browser safety prompts such as "This web page is trying to print."
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        return fileName;
    }

    win.focus?.();
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    return fileName;
};

/** Shared exit path for generate*PDF helpers (save, print, or return doc for portal blob export). */
const emitPdfResult = (doc, fileName, { forPrint = false, returnDoc = false } = {}) => {
    if (returnDoc) return doc;
    if (forPrint) {
        return printPdfDoc(doc, fileName);
    }
    doc.save(fileName);
    return fileName;
};

/**
 * Add header to PDF document
 */
const addHeader = (doc, pageWidth, isArabic = false) => {
    const centerX = getContentCenterX(pageWidth);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(isArabic ? CLINIC_INFO.nameAr : CLINIC_INFO.name, centerX, pdfPageY(15), { align: 'center', maxWidth: getContentWidth(pageWidth) });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(isArabic ? CLINIC_INFO.title1Ar : CLINIC_INFO.title1, centerX, pdfPageY(22), { align: 'center', maxWidth: getContentWidth(pageWidth) });
    doc.text(isArabic ? CLINIC_INFO.title2Ar : CLINIC_INFO.title2, centerX, pdfPageY(27), { align: 'center', maxWidth: getContentWidth(pageWidth) });
    doc.text(isArabic ? CLINIC_INFO.title3Ar : CLINIC_INFO.title3, centerX, pdfPageY(32), { align: 'center', maxWidth: getContentWidth(pageWidth) });

    // Divider line
    doc.setDrawColor(0, 128, 128);
    doc.setLineWidth(0.5);
    doc.line(PRINT_MARGIN_LEFT, pdfPageY(38), pageWidth - PRINT_MARGIN_RIGHT, pdfPageY(38));
};

/**
 * Add footer to PDF document (clinic telephone and address only) at a fixed Y.
 */
const addFooter = (doc, pageWidth, isArabic = false) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    const { footerTelY, footerAddrY } = getPdfBottomLayout(pageHeight);
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);

    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Tel: ${CLINIC_INFO.phone}`, centerX, footerTelY, { align: 'center', maxWidth: contentWidth });
    doc.text(isArabic ? CLINIC_INFO.addressAr : CLINIC_INFO.address, centerX, footerAddrY, { align: 'center', maxWidth: contentWidth });
    doc.setTextColor(0);
};

const getPatientName = (patient) => {
    if (!patient) return 'Unknown Patient';
    if (typeof patient === 'string') return patient;
    return patient.name || patient.patientName || 'Unknown Patient';
};

const getPatientFileName = (patient) => getPatientName(patient).replace(/\s+/g, '_');

/** Prefer explicit PDF ids and ids on the clinical row; then id on a patient object passed as the display arg. */
const coalescePdfPatientReferenceId = (record, patientArg, options = {}) => {
    const from = resolvePatientFileIdForPdf(options, record);
    if (from != null && String(from).trim() !== '') return String(from).trim();
    return getPatientId(patientArg);
};


const formatMedicationInstructionsForPdf = (med, isArabic = false) => {
    const text = String(med?.instructions || '').trim();
    if (!text) return '';
    const label = isArabic ? 'التعليمات:' : 'Instructions:';
    const body = isArabic ? (translateMedicationInstruction(text) || text) : text;
    return `${label} ${body}`;
};

const buildMedicationPdfTableRow = (med, idx, isArabic = false) => {
    const name = [med.name, med.genericName || med.generic, med.type ? `(${med.type})` : '']
        .filter(Boolean)
        .join(' ');
    const instructions = formatMedicationInstructionsForPdf(med, isArabic);
    const medicationCell = [name, instructions].filter(Boolean).join('\n') || '-';
    const frequency = med.frequency || '-';
    const duration = med.duration || '-';
    return [
        (idx + 1).toString(),
        medicationCell,
        isArabic ? (translateMedicationInstruction(frequency) || frequency) : frequency,
        isArabic ? (translateMedicationInstruction(duration) || duration) : duration,
    ];
};

/** Visit- or prescription-level instructions (not tied to a single medication row). */
const collectVisitLevelInstructions = (medications = [], options = {}) => {
    const { visitInstructions = null } = options;
    const blocks = [];
    const visitText = String(visitInstructions || '').trim();
    if (visitText) blocks.push(visitText);
    medications.forEach((med) => {
        const text = String(med?.visitInstructions || med?.prescriptionInstructions || '').trim();
        if (text && !blocks.includes(text)) blocks.push(text);
    });
    return blocks;
};

const formatRegimenSteps = (med, isArabic = false) => {
    const steps = [
        { frequency: med.frequency, duration: med.duration },
        ...(Array.isArray(med.regimenEntries) ? med.regimenEntries : []),
    ]
        .map((step) => ({
            frequency: String(step?.frequency || '').trim(),
            duration: String(step?.duration || '').trim(),
        }))
        .filter((step) => step.frequency || step.duration);

    if (steps.length === 0) {
        return isArabic ? 'حسب توجيهات الطبيب' : 'as directed';
    }
    return steps
        .map((step) => {
            const freq = step.frequency || (isArabic ? 'حسب التوجيه' : 'as directed');
            const freqText = isArabic ? (translateMedicationInstruction(freq) || freq) : freq;
            const durationText = step.duration
                ? (isArabic
                    ? `لمدة ${translateMedicationInstruction(step.duration) || step.duration}`
                    : `for ${step.duration}`)
                : '';
            return [freqText, durationText].filter(Boolean).join(' ');
        })
        .join(isArabic ? ' ثم ' : ' -> ');
};

const isTopicalSteroidMedication = (med) => {
    if (!med || typeof med !== 'object') return false;
    const haystack = [
        med.type,
        med.name,
        med.genericName,
        med.generic,
        med.category,
    ].filter(Boolean).join(' ').toLowerCase();
    const isTopical = /eye|drop|ointment|ophthalmic/.test(haystack);
    const isSteroid = med.isSteroid || /steroid|prednisolone|dexamethasone|fluorometholone|loteprednol|rimexolone|hydrocortisone|tobradex|maxitrol|fml/.test(haystack);
    return Boolean(isTopical && isSteroid);
};

const getSteroidStopReminder = (isArabic = false) => (
    isArabic
        ? 'توقف بعد انتهاء المدة الموصوفة — لا تستمر في القطرات بعد انتهاء الجدول إلا بإرشاد الطبيب.'
        : 'Stop after the prescribed course; do not continue the drops beyond this schedule unless your doctor advises you.'
);

const formatSteroidRegimen = (med, isArabic = false) => {
    const name = med.name || (isArabic ? 'ستيرويد' : 'Steroid');
    const regimen = formatRegimenSteps(med, isArabic);
    const taper = String(med.tapering || '').trim();
    const taperText = taper
        ? (isArabic ? (translateMedicationInstruction(taper) || taper) : taper)
        : '';
    const stopReminder = getSteroidStopReminder(isArabic);
    return `${name}: ${regimen}${taperText ? ` | ${taperText}` : ''}. ${stopReminder}`;
};

/** Render steroid regimen + visit-level instructions below the medication table. */
const appendMedicationPdfNotes = (doc, medications, startY, contentWidth, options = {}) => {
    const {
        dense = false,
        isArabic = false,
        visitInstructions = null,
    } = options;
    let y = startY;
    const labelSize = dense ? 8 : 9;
    const bodySize = dense ? 7 : 8;
    const lineStep = dense ? 4.3 : 5;

    doc.setFont('helvetica', 'normal');

    const steroidMeds = medications.filter(isTopicalSteroidMedication);
    if (steroidMeds.length > 0) {
        doc.setCharSpace(0);
        doc.setFontSize(labelSize);
        doc.text(
            isArabic ? 'نظام الستيرويد:' : 'Steroid regimen:',
            PRINT_MARGIN_LEFT,
            y,
            { charSpace: 0, maxWidth: contentWidth }
        );
        doc.setFontSize(bodySize);
        y += 5;
        steroidMeds.forEach((med, idx) => {
            const line = `${idx + 1}. ${formatSteroidRegimen(med, isArabic)}`;
            const splitLines = doc.splitTextToSize(line, contentWidth);
            doc.text(splitLines, PRINT_MARGIN_LEFT, y, { maxWidth: contentWidth, charSpace: 0 });
            y += splitLines.length * lineStep;
        });
        y += dense ? 2 : 4;
    }

    const visitInstructionBlocks = collectVisitLevelInstructions(medications, { visitInstructions });
    if (visitInstructionBlocks.length > 0) {
        doc.setCharSpace(0);
        doc.setFontSize(labelSize);
        doc.text(
            isArabic ? 'تعليمات الزيارة:' : 'Visit instructions:',
            PRINT_MARGIN_LEFT,
            y,
            { charSpace: 0, maxWidth: contentWidth }
        );
        doc.setFontSize(bodySize);
        y += 5;
        visitInstructionBlocks.forEach((block) => {
            const text = isArabic ? (translateMedicationInstruction(block) || block) : block;
            const splitLines = doc.splitTextToSize(text, contentWidth);
            const pageWidth = doc.internal.pageSize.getWidth();
            const needed = splitLines.length * lineStep + (dense ? 1 : 2);
            y = ensurePdfVerticalSpace(doc, pageWidth, isArabic, y, needed + 4);
            doc.text(splitLines, PRINT_MARGIN_LEFT, y, { maxWidth: contentWidth, charSpace: 0 });
            y += needed;
        });
    }

    return y;
};

/** Merge resolved short id into a string or partial patient object for headers, filenames, and QR. */
const normalizePatientArgForPdf = (patientArg, referenceId) => {
    const id = referenceId != null && String(referenceId).trim() !== '' ? String(referenceId).trim() : null;
    if (patientArg != null && typeof patientArg === 'object') {
        const name = getPatientName(patientArg);
        const mergedId = getPatientId(patientArg) || id;
        return {
            ...patientArg,
            name,
            patientName: name,
            id: mergedId,
            patientId: patientArg.patientId ?? mergedId,
        };
    }
    const nameStr = patientArg != null && String(patientArg).trim() !== '' ? String(patientArg) : 'Unknown Patient';
    return { name: nameStr, patientName: nameStr, id, patientId: id };
};

const addPatientInfoBlock = async (doc, patient, x, y, options = {}) => {
    const {
        isArabic = false,
        recordDate = '',
        pageWidth = doc.internal.pageSize.getWidth(),
        patientId: optPatientId,
        referenceId: optReferenceId,
        sourceRecord = null,
        drawHeaderQrs = true,
    } = options;
    const dateLabel = isArabic ? 'التاريخ:' : 'Date:';
    const refLabel = isArabic ? 'رقم المرجع:' : 'REFERENCE ID:';
    const refFromRecord = patientIdFromRecord(sourceRecord);
    const refValue = resolvePortalPatientId(patient, {
        patientId: optPatientId ?? refFromRecord,
        referenceId: optReferenceId ?? refFromRecord,
    });
    const rows = [
        `${isArabic ? 'المريض:' : 'Patient:'} ${getPatientName(patient)}`,
        ...(recordDate ? [`${dateLabel} ${recordDate}`] : []),
        ...(refValue ? [`${refLabel} ${refValue}`] : []),
    ].filter(Boolean);

    const qrLayout = drawHeaderQrs ? getPatientHeaderQrLayout(pageWidth, y) : null;
    const textMaxWidth = qrLayout?.textMaxWidth ?? (pageWidth - x - PRINT_MARGIN_RIGHT);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    rows.forEach((row, index) => doc.text(row, x, y + (index * 6), { maxWidth: textMaxWidth }));

    const textEndY = y + (rows.length * 6);
    if (qrLayout) {
        await addPatientHeaderQRs(doc, qrLayout, refValue);
        return Math.max(textEndY, qrLayout.blockBottomY) + 4;
    }
    return textEndY + 4;
};

/**
 * Generate a PDF for glasses prescription with improved formatting
 */
export const generateGlassesPDF = async (glasses, patientName, options = {}) => {
    const { isArabic = false, patientId = null, referenceId = null } = options;
    const portalId = coalescePdfPatientReferenceId(glasses, patientName, { patientId, referenceId });
    const patientForPdf = normalizePatientArgForPdf(patientName, portalId);
    const doc = createPdfDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);

    // Header
    addHeader(doc, pageWidth, isArabic);

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 128, 128);
    const title = glasses.glassesType === 'old'
        ? (isArabic ? 'وصفة النظارة القديمة' : 'OLD GLASSES PRESCRIPTION')
        : (isArabic ? 'وصفة النظارة الطبية' : 'OPTICAL PRESCRIPTION');
    doc.text(title, centerX, pdfPageY(50), { align: 'center', maxWidth: contentWidth });
    doc.setTextColor(0, 0, 0);

    // Type badge
    if (glasses.glassesType === 'old') {
        doc.setFillColor(254, 243, 199);
        doc.roundedRect(centerX - 25, pdfPageY(54), 50, 8, 2, 2, 'F');
        doc.setFontSize(8);
        doc.setTextColor(146, 64, 14);
        doc.text(isArabic ? 'سجل تاريخي' : 'Historical Record', centerX, pdfPageY(59), { align: 'center' });
        doc.setTextColor(0, 0, 0);
    }

    // Patient Info (Rx ID intentionally omitted - not meaningful to the patient)
    const infoY = pdfPageY(70);
    const afterPatientBlock = await addPatientInfoBlock(doc, patientForPdf, PRINT_MARGIN_LEFT, infoY, {
        isArabic,
        pageWidth,
        recordDate: glasses.date || new Date().toISOString().split('T')[0],
        patientId: portalId,
        referenceId: portalId,
        sourceRecord: glasses,
    });

    // Prescription Table - wider columns so every cell renders in a single line
    const tableData = [
        [
            isArabic ? 'العين اليمنى (OD)' : 'Right Eye (OD)',
            glasses.sph_right || '-',
            glasses.cyl_right || '-',
            glasses.axis_right || '-',
            glasses.va_right || '-',
            glasses.add_power || '-'
        ],
        [
            isArabic ? 'العين اليسرى (OS)' : 'Left Eye (OS)',
            glasses.sph_left || '-',
            glasses.cyl_left || '-',
            glasses.axis_left || '-',
            glasses.va_left || '-',
            glasses.add_power || '-'
        ]
    ];

    const tableWidth = contentWidth;
    // Allocate: eye label 60mm, then 5 equal data columns share remaining width
    const eyeColWidth = 36;
    const dataColWidth = (tableWidth - eyeColWidth) / 5;

    autoTable(doc, {
        startY: afterPatientBlock + 8,
        head: [[
            isArabic ? 'العين' : 'Eye',
            isArabic ? 'الكرة' : 'SPH',
            isArabic ? 'الأسطوانة' : 'CYL',
            isArabic ? 'المحور' : 'Axis',
            'VA',
            isArabic ? 'الإضافة' : 'ADD'
        ]],
        body: tableData,
        theme: 'grid',
        tableWidth,
        styles: {
            ...compactTableStyles(10),
        },
        headStyles: {
            ...compactHeadStyles(10),
            fontStyle: 'bold',
            halign: 'center',
        },
        bodyStyles: {
            fontSize: 11,
            halign: 'center',
            cellPadding: { top: 2, right: 1.5, bottom: 2, left: 1.5 },
            font: 'helvetica',
            overflow: 'ellipsize',
            minCellHeight: 6,
        },
        columnStyles: {
            0: { fontStyle: 'bold', halign: 'left', cellWidth: eyeColWidth, fontSize: 12 },
            1: { cellWidth: dataColWidth },
            2: { cellWidth: dataColWidth },
            3: { cellWidth: dataColWidth },
            4: { cellWidth: dataColWidth },
            5: { cellWidth: dataColWidth }
        },
        alternateRowStyles: {
            fillColor: [248, 250, 252]
        },
        margin: getPdfTableMargins(doc),
    });

    const finalY = doc.lastAutoTable.finalY + 15;
    let sectionY = finalY;

    if (glasses.contactLensEnabled) {
        const clType =
            glasses.contactLensType === 'toric'
                ? isArabic
                    ? 'طورية'
                    : 'Toric'
                : isArabic
                  ? 'قياسية (كروية)'
                  : 'Standard (spherical)';
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(
            isArabic ? `عدسات لاصقة مقترحة (${clType}):` : `Suggested contact lenses (${clType}):`,
            PRINT_MARGIN_LEFT,
            sectionY
        );
        sectionY += 10;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        const clOd = `OD: ${glasses.cl_sph_right || '-'} / ${glasses.cl_cyl_right || '-'}${glasses.cl_axis_right ? ` × ${glasses.cl_axis_right}°` : ''}`;
        const clOs = `OS: ${glasses.cl_sph_left || '-'} / ${glasses.cl_cyl_left || '-'}${glasses.cl_axis_left ? ` × ${glasses.cl_axis_left}°` : ''}`;
        doc.text(`${clOd}    ${clOs}`, PRINT_MARGIN_LEFT, sectionY);
        sectionY += 14;
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(
            isArabic
                ? 'محسوبة من وصفة النظارات (تعويض مسافة الرأس 12 مم) — تحقق بالتجربة.'
                : 'Derived from spectacles Rx (12 mm vertex) — confirm with trial lenses.',
            PRINT_MARGIN_LEFT,
            sectionY
        );
        doc.setTextColor(0);
        sectionY += 12;
    }

    // PD Section
    if (glasses.pd) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(`${isArabic ? 'المسافة الحدقية (PD):' : 'Pupillary Distance (PD):'} ${glasses.pd}`, PRINT_MARGIN_LEFT, sectionY);
        sectionY += 15;
    }

    // Notes section
    const notesY = sectionY;
    if (glasses.notes) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(isArabic ? 'ملاحظات:' : 'Notes:', PRINT_MARGIN_LEFT, notesY);
        doc.setFont('helvetica', 'normal');
        doc.text(glasses.notes, PRINT_MARGIN_LEFT, notesY + 8, { maxWidth: contentWidth });
    }

    const contentEndY = glasses.notes
        ? notesY + 8 + doc.getTextDimensions(glasses.notes, { maxWidth: contentWidth }).h
        : sectionY;
    await finalizePatientReportPdf(doc, pageWidth, isArabic, portalId, contentEndY);

    return doc;
};

/**
 * Print glasses prescription (opens print dialog)
 */
export const printGlassesPDF = async (glasses, patientName, isArabicOrOptions = false) => {
    const opt = mergeArabicOrPdfOptions(isArabicOrOptions);
    const doc = await generateGlassesPDF(glasses, patientName, opt);
    const portalId = coalescePdfPatientReferenceId(glasses, patientName, opt);
    const patientForPdf = normalizePatientArgForPdf(patientName, portalId);
    const fileName = `glasses_${glasses.glassesType || 'new'}_${getPatientFileName(patientForPdf)}_${glasses.id}.pdf`;
    return printPdfDoc(doc, fileName);
};

/**
 * Download glasses prescription (prompts for save location)
 */
export const downloadGlassesPDF = async (glasses, patientName, isArabicOrOptions = false) => {
    const opt = mergeArabicOrPdfOptions(isArabicOrOptions);
    const doc = await generateGlassesPDF(glasses, patientName, opt);
    const portalId = coalescePdfPatientReferenceId(glasses, patientName, opt);
    const patientForPdf = normalizePatientArgForPdf(patientName, portalId);
    const fileName = `glasses_${glasses.glassesType || 'new'}_${getPatientFileName(patientForPdf)}_${glasses.id}.pdf`;
    doc.save(fileName);
    return fileName;
};

// Legacy function for backward compatibility
export const generatePrescriptionPDF = (prescription, patientName) => {
    return downloadGlassesPDF(prescription, patientName);
};

/**
 * Generate a PDF report for a patient's visit/examination
 */
export const generateExaminationPDF = async (exam, patientName, options = {}) => {
    const { forPrint = false, returnDoc = false, patientId = null, referenceId = null } = options;
    const portalId = coalescePdfPatientReferenceId(exam, patientName, { patientId, referenceId });
    const patientForPdf = normalizePatientArgForPdf(patientName, portalId);
    const doc = createPdfDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);

    addHeader(doc, pageWidth);

    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('EYE EXAMINATION REPORT', centerX, pdfPageY(48), { align: 'center', maxWidth: contentWidth });

    // Patient Info
    let currentY = await addPatientInfoBlock(doc, patientForPdf, PRINT_MARGIN_LEFT, pdfPageY(58), {
        pageWidth,
        recordDate: exam.date || exam.createdAt?.split('T')[0],
        patientId: portalId,
        referenceId: portalId,
        sourceRecord: exam,
    });

    // Visual Acuity
    if (exam.unaided_va) {
        doc.setFont('helvetica', 'bold');
        doc.text('Visual Acuity (Unaided):', PRINT_MARGIN_LEFT, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(
            `OD: ${exam.unaided_va?.right || '-'}   OS: ${exam.unaided_va?.left || '-'}`,
            PRINT_MARGIN_LEFT + 50,
            currentY,
            { maxWidth: contentWidth - 50 }
        );
        currentY += 10;
    }

    // Refraction
    if (exam.subjective) {
        doc.setFont('helvetica', 'bold');
        doc.text('Subjective Refraction:', PRINT_MARGIN_LEFT, currentY);
        currentY += 7;

        const refractionData = [
            ['Eye', 'Sph', 'Cyl', 'Axis', 'VA'],
            ['OD', exam.subjective.right?.sph || '-', exam.subjective.right?.cyl || '-', exam.subjective.right?.axis || '-', exam.subjective.right?.va || '-'],
            ['OS', exam.subjective.left?.sph || '-', exam.subjective.left?.cyl || '-', exam.subjective.left?.axis || '-', exam.subjective.left?.va || '-']
        ];

        autoTable(doc, {
            startY: currentY,
            head: [refractionData[0]],
            body: refractionData.slice(1),
            theme: 'striped',
            headStyles: compactHeadStyles(8),
            styles: { ...compactTableStyles(8), halign: 'center' },
            margin: getPdfTableMargins(doc),
        });
        currentY = doc.lastAutoTable.finalY + 10;
    }

    // IOP
    if (exam.iop) {
        doc.setFont('helvetica', 'bold');
        doc.text('Intraocular Pressure:', PRINT_MARGIN_LEFT, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(`OD: ${exam.iop.right || '-'} mmHg   OS: ${exam.iop.left || '-'} mmHg`, PRINT_MARGIN_LEFT + 50, currentY, { maxWidth: contentWidth - 50 });
        currentY += 12;
    }

    const clinicalSections = [
        ['Lids & Lashes', formatFindingPairForDisplay(exam.lids)],
        ['Adnexa & Orbit', formatFindingPairForDisplay(exam.adnexa)],
        ['Anterior Segment', formatFindingPairForDisplay(exam.anterior_segment)],
        ['Pupils', formatFindingPairForDisplay(exam.pupils)],
        ['Ocular Motility', formatFindingPairForDisplay(exam.ocular_motility)],
        ['Posterior Segment (Fundus)', formatFindingPairForDisplay(exam.posterior_segment)],
        ['Neuro-ophthalmic', formatFindingPairForDisplay(exam.neuro_ophthalmic)],
    ];
    for (const [label, value] of clinicalSections) {
        if (!value) continue;
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, PRINT_MARGIN_LEFT, currentY);
        currentY += 7;
        doc.setFont('helvetica', 'normal');
        doc.text(value, PRINT_MARGIN_LEFT, currentY, { maxWidth: contentWidth });
        currentY += doc.getTextDimensions(value, { maxWidth: contentWidth }).h + 8;
    }

    // Diagnosis
    if (exam.diagnosis) {
        doc.setFont('helvetica', 'bold');
        doc.text('Diagnosis:', PRINT_MARGIN_LEFT, currentY);
        currentY += 7;
        doc.setFont('helvetica', 'normal');
        doc.text(exam.diagnosis, PRINT_MARGIN_LEFT, currentY, { maxWidth: contentWidth });
        currentY += doc.getTextDimensions(exam.diagnosis, { maxWidth: contentWidth }).h + 10;
    }

    // Notes
    if (exam.notes) {
        doc.setFont('helvetica', 'bold');
        doc.text('Additional Notes:', PRINT_MARGIN_LEFT, currentY);
        currentY += 7;
        doc.setFont('helvetica', 'normal');
        doc.text(exam.notes, PRINT_MARGIN_LEFT, currentY, { maxWidth: contentWidth });
        currentY += doc.getTextDimensions(exam.notes, { maxWidth: contentWidth }).h + 8;
    }

    await finalizePatientReportPdf(doc, pageWidth, false, portalId, currentY);

    const fileName = `exam_report_${getPatientFileName(patientForPdf)}_${exam.id || Date.now()}.pdf`;
    return emitPdfResult(doc, fileName, { forPrint, returnDoc });
};

export const printExaminationPDF = (exam, patientName, opts = {}) => {
    return generateExaminationPDF(exam, patientName, { ...mergeExamPdfOptions(opts), forPrint: true });
};

export const downloadExaminationPDF = (exam, patientName, opts = {}) => {
    return generateExaminationPDF(exam, patientName, { ...mergeExamPdfOptions(opts), forPrint: false });
};

/**
 * Generate a medication prescription PDF with Arabic support
 */
export const generateMedicationPDF = async (medications, patientName, options = {}) => {
    const { isArabic = false, forPrint = false, returnDoc = false, patientId = null, referenceId = null } = options;
    const medsArray = Array.isArray(medications) ? medications : [medications];
    const portalId = coalescePdfPatientReferenceId(medsArray[0], patientName, { patientId, referenceId });
    const patientForPdf = normalizePatientArgForPdf(patientName, portalId);
    const doc = createPdfDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);
    const t = (key) => translations[isArabic ? 'ar' : 'en'][key] || key;

    addHeader(doc, pageWidth, isArabic);

    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(t('medicationPrescription'), centerX, pdfPageY(48), { align: 'center', maxWidth: contentWidth });

    // Patient Info
    const patientBlockEndY = await addPatientInfoBlock(doc, patientForPdf, PRINT_MARGIN_LEFT, pdfPageY(58), {
        isArabic,
        pageWidth,
        recordDate: new Date().toISOString().split('T')[0],
        patientId: portalId,
        referenceId: portalId,
        sourceRecord: medsArray[0],
    });

    // Medications: one autoTable — single head row, one body row per medication (never split into multiple tables)
    const tableData = medsArray.map((med, idx) => buildMedicationPdfTableRow(med, idx, isArabic));

    if (medsArray.length > 0) {
        const layout = getMedicationTableLayout(medsArray.length, contentWidth);
        const [cw0, cw1, cw2, cw3] = layout.columnWidths;

        autoTable(doc, {
            startY: patientBlockEndY + 4,
            head: [[
                '#',
                t('medication'),
                t('frequency'),
                t('duration')
            ]],
            body: tableData,
            theme: 'grid',
            tableWidth: contentWidth,
            styles: {
                fontSize: layout.bodyFont,
                cellPadding: layout.cellPadding,
                overflow: 'linebreak',
                minCellHeight: layout.minCellHeight,
                valign: 'top',
            },
            headStyles: {
                ...neutralMedicationHeadStyles(layout.headFont),
                fontSize: layout.headFont,
                cellPadding: layout.cellPadding,
            },
            bodyStyles: {
                fontSize: layout.bodyFont,
                cellPadding: layout.cellPadding,
                overflow: 'linebreak',
                minCellHeight: layout.minCellHeight,
                valign: 'top',
            },
            columnStyles: {
                0: { cellWidth: cw0, halign: 'center' },
                1: { cellWidth: cw1, overflow: 'linebreak' },
                2: { cellWidth: cw2, overflow: 'linebreak' },
                3: { cellWidth: cw3, halign: 'center', overflow: 'linebreak' }
            },
            margin: getPdfTableMargins(doc),
        });
    }

    let contentEndY = doc.lastAutoTable?.finalY ?? patientBlockEndY;
    const hasNotes = medsArray.some(isTopicalSteroidMedication)
        || collectVisitLevelInstructions(medsArray, options).length > 0;
    if (hasNotes) {
        const labelY = (doc.lastAutoTable?.finalY ?? patientBlockEndY) + (medsArray.length > 6 ? 8 : 10);
        contentEndY = appendMedicationPdfNotes(doc, medsArray, labelY, contentWidth, {
            dense: medsArray.length > 6,
            isArabic,
            visitInstructions: options.visitInstructions,
        });
    }

    await finalizePatientReportPdf(doc, pageWidth, isArabic, portalId, contentEndY);

    const fileName = `medication_rx_${getPatientFileName(patientForPdf)}_${Date.now()}.pdf`;
    return emitPdfResult(doc, fileName, { forPrint, returnDoc });
};

/**
 * Print medication PDF
 */
export const printMedicationPDF = (medications, patientName, isArabicOrOptions = false) => {
    return generateMedicationPDF(medications, patientName, { ...mergeArabicOrPdfOptions(isArabicOrOptions), forPrint: true });
};

/**
 * Download medication PDF
 */
export const downloadMedicationPDF = (medications, patientName, isArabicOrOptions = false) => {
    return generateMedicationPDF(medications, patientName, { ...mergeArabicOrPdfOptions(isArabicOrOptions), forPrint: false });
};

/**
 * Generate a PDF for Investigation with Arabic support
 */
export const generateInvestigationPDF = async (inv, patientName, options = {}) => {
    const { isArabic = false, forPrint = false, returnDoc = false, patientId = null, referenceId = null } = options;
    const portalId = coalescePdfPatientReferenceId(inv, patientName, { patientId, referenceId });
    const patientForPdf = normalizePatientArgForPdf(patientName, portalId);
    const doc = createPdfDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);

    addHeader(doc, pageWidth, isArabic);

    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    const title = isArabic ? 'طلب / تقرير الفحوصات' : 'INVESTIGATION REQUEST / REPORT';
    doc.text(title, centerX, pdfPageY(52), { align: 'center', maxWidth: contentWidth });

    // Patient Info
    let currentY = await addPatientInfoBlock(doc, patientForPdf, PRINT_MARGIN_LEFT, pdfPageY(62), {
        isArabic,
        pageWidth,
        recordDate: inv.date || new Date().toISOString().split('T')[0],
        patientId: portalId,
        referenceId: portalId,
        sourceRecord: inv,
    });

    doc.setFont('helvetica', 'bold');
    doc.text(`${isArabic ? 'النوع:' : 'Type:'} ${inv.type}`, PRINT_MARGIN_LEFT, currentY, { maxWidth: contentWidth });
    currentY += 10;

    if (inv.details) {
        doc.text(isArabic ? 'التفاصيل:' : 'Details:', PRINT_MARGIN_LEFT, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(inv.details, PRINT_MARGIN_LEFT, currentY + 7, { maxWidth: contentWidth });
        currentY += 20;
    }

    if (inv.results) {
        doc.setFont('helvetica', 'bold');
        doc.text(isArabic ? 'النتائج:' : 'Results:', PRINT_MARGIN_LEFT, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(inv.results, PRINT_MARGIN_LEFT, currentY + 7, { maxWidth: contentWidth });
        currentY += 20;
    }

    if (inv.recommendations) {
        doc.setFont('helvetica', 'bold');
        doc.text(isArabic ? 'التوصيات:' : 'Recommendations:', PRINT_MARGIN_LEFT, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(inv.recommendations, PRINT_MARGIN_LEFT, currentY + 7, { maxWidth: contentWidth });
        currentY += 20;
    }

    await finalizePatientReportPdf(doc, pageWidth, isArabic, portalId, currentY);

    const fileName = `investigation_${getPatientFileName(patientForPdf)}_${inv.id}.pdf`;
    return emitPdfResult(doc, fileName, { forPrint, returnDoc });
};

/**
 * Print investigation PDF
 */
export const printInvestigationPDF = (inv, patientName, isArabicOrOptions = false) => {
    return generateInvestigationPDF(inv, patientName, { ...mergeArabicOrPdfOptions(isArabicOrOptions), forPrint: true });
};

/**
 * Download investigation PDF
 */
export const downloadInvestigationPDF = (inv, patientName, isArabicOrOptions = false) => {
    return generateInvestigationPDF(inv, patientName, { ...mergeArabicOrPdfOptions(isArabicOrOptions), forPrint: false });
};

/**
 * Generate a PDF for Treatment Plan with Arabic support
 */
export const generateTreatmentPDF = async (treatment, patientName, options = {}) => {
    const { isArabic = false, forPrint = false, returnDoc = false, patientId = null, referenceId = null } = options;
    const portalId = coalescePdfPatientReferenceId(treatment, patientName, { patientId, referenceId });
    const patientForPdf = normalizePatientArgForPdf(patientName, portalId);
    const doc = createPdfDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);

    addHeader(doc, pageWidth, isArabic);

    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    const title = isArabic ? 'خطة العلاج' : 'TREATMENT PLAN';
    doc.text(title, centerX, pdfPageY(50), { align: 'center', maxWidth: contentWidth });

    // Patient Info
    let currentY = await addPatientInfoBlock(doc, patientForPdf, PRINT_MARGIN_LEFT, pdfPageY(60), {
        isArabic,
        pageWidth,
        recordDate: treatment.date || new Date().toISOString().split('T')[0],
        patientId: portalId,
        referenceId: portalId,
        sourceRecord: treatment,
    });

    if (treatment.medical_treatment || treatment.medicalTreatment) {
        currentY = writePdfLabeledFreeText(
            doc,
            isArabic ? 'العلاج الدوائي:' : 'Medical Treatment:',
            treatment.medical_treatment || treatment.medicalTreatment,
            PRINT_MARGIN_LEFT,
            currentY,
            contentWidth,
            { sectionGap: 8 }
        );
    }

    if (treatment.investigations) {
        currentY = writePdfLabeledFreeText(
            doc,
            isArabic ? 'الفحوصات المطلوبة:' : 'Investigations:',
            treatment.investigations,
            PRINT_MARGIN_LEFT,
            currentY,
            contentWidth,
            { sectionGap: 5 }
        );
    }

    if (treatment.surgical_treatment || treatment.surgicalTreatment) {
        currentY = writePdfLabeledFreeText(
            doc,
            isArabic ? 'العلاج الجراحي:' : 'Surgical Treatment:',
            treatment.surgical_treatment || treatment.surgicalTreatment,
            PRINT_MARGIN_LEFT,
            currentY,
            contentWidth,
            { sectionGap: 8 }
        );
    }

    if (treatment.followup_date || treatment.followupDate) {
        doc.setFont('helvetica', 'bold');
        doc.text(`${isArabic ? 'موعد المتابعة:' : 'Follow-up Date:'} ${treatment.followup_date || treatment.followupDate}`, PRINT_MARGIN_LEFT, currentY, { maxWidth: contentWidth });
        currentY += 15;
    }

    if (treatment.notes) {
        currentY = writePdfLabeledFreeText(
            doc,
            isArabic ? 'ملاحظات:' : 'Notes:',
            treatment.notes,
            PRINT_MARGIN_LEFT,
            currentY,
            contentWidth,
            { sectionGap: 0 }
        );
    }

    await finalizePatientReportPdf(doc, pageWidth, isArabic, portalId, currentY);

    const fileName = `treatment_${getPatientFileName(patientForPdf)}_${treatment.id}.pdf`;
    return emitPdfResult(doc, fileName, { forPrint, returnDoc });
};

/**
 * Print treatment PDF
 */
export const printTreatmentPDF = (treatment, patientName, isArabicOrOptions = false) => {
    return generateTreatmentPDF(treatment, patientName, { ...mergeArabicOrPdfOptions(isArabicOrOptions), forPrint: true });
};

/**
 * Download treatment PDF
 */
export const downloadTreatmentPDF = (treatment, patientName, isArabicOrOptions = false) => {
    return generateTreatmentPDF(treatment, patientName, { ...mergeArabicOrPdfOptions(isArabicOrOptions), forPrint: false });
};

const formatMedTreatDate = (item) => item?.date_prescribed || item?.date || item?.createdAt?.split?.('T')?.[0] || '-';

/** Stable key so we can tell if a Med&Treat export list is for one patient only. */
const getMedTreatPatientKey = (item) => {
    const id = item?.patientId ?? item?.patient_id;
    if (id != null && String(id).trim() !== '') return `id:${String(id)}`;
    const name = (item?.patientName || '').trim().toLowerCase();
    return name ? `name:${name}` : '';
};

const allMedTreatItemsSamePatient = (items) => {
    if (!Array.isArray(items) || items.length === 0) return false;
    const keys = items.map(getMedTreatPatientKey);
    if (keys.some((k) => !k)) return false;
    return keys.every((k) => k === keys[0]);
};

/**
 * Typography for the summary tables (global 4-col or single-patient 3-col non-med block).
 * Scaled by row count so long lists stay on A5 where possible.
 */
const getMedTreatSummaryTableLayout = (rowCount) => {
    let fontSize = 7;
    let headFont = 7;
    let cellPadding = COMPACT_CELL_PADDING;
    let minCellHeight = 4.6;
    if (rowCount > 12) {
        fontSize = 5.5;
        headFont = 6;
        cellPadding = { top: 1, right: 1, bottom: 1, left: 1 };
        minCellHeight = 3.2;
    } else if (rowCount > 8) {
        fontSize = 6;
        headFont = 6.5;
        cellPadding = { top: 1.1, right: 1.1, bottom: 1.1, left: 1.1 };
        minCellHeight = 3.5;
    }
    return { fontSize, headFont, cellPadding, minCellHeight };
};

const formatMedTreatEntry = (item, isArabic = false) => {
    if (item.itemType === 'medication') {
        const steroidRegimen = isTopicalSteroidMedication(item)
            ? `Steroid regimen: ${formatSteroidRegimen(item, isArabic)}`
            : '';
        const medInstructions = formatMedicationInstructionsForPdf(item, isArabic);
        return [
            item.name || 'Medication',
            item.genericName || item.generic || '',
            [item.frequency, item.duration].filter(Boolean).join(' | '),
            steroidRegimen,
            medInstructions,
            item.notes || '',
        ].filter(Boolean).join(' | ');
    }

    return [
        item.surgical_treatment || item.surgicalTreatment ? 'Surgical Plan' : 'Medical Treatment',
        item.medical_treatment || item.medicalTreatment || '',
        item.surgical_treatment || item.surgicalTreatment || '',
        item.investigations ? `Investigations: ${item.investigations}` : '',
        item.followup_date || item.followupDate ? `Follow-up: ${item.followup_date || item.followupDate}` : '',
        item.notes || '',
    ].filter(Boolean).join(' | ');
};

const medTreatOtherSummaryRow = (item, isArabic = false) => [
    formatMedTreatDate(item),
    item.itemType === 'medication' ? 'Medication' : item.itemType === 'surgical' ? 'Surgical' : 'Treatment',
    formatMedTreatEntry(item, isArabic),
];

/**
 * Generate the current Medications & Treatments section/list as one A5 PDF.
 * Single-patient lists: one Rx-style medication table (all meds) first when present,
 * then a Date / Type / Details table for non-medication rows. Mixed-patient lists
 * keep the global Date / Patient / Type / Details table.
 */
export const generateMedicationsTreatmentsPDF = async (items = [], title = 'Medications & Treatments', options = {}) => {
    const { forPrint = false, returnDoc = false, isArabic = false, patientId: optPatientId = null, referenceId: optReferenceId = null } = options;
    const doc = createPdfDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);
    const t = (key) => translations[isArabic ? 'ar' : 'en'][key] || key;

    addHeader(doc, pageWidth, isArabic);
    doc.setCharSpace(0);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(title.toUpperCase(), centerX, pdfPageY(50), { align: 'center', maxWidth: contentWidth, charSpace: 0 });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Printed: ${new Date().toLocaleString()}`, PRINT_MARGIN_LEFT, pdfPageY(59), { maxWidth: contentWidth, charSpace: 0 });

    let lastTableFinalY = null;
    const printedLineBottomY = pdfPageY(59) + 7;
    let tableStartY = pdfPageY(72);

    const runMixedPatientTable = () => {
        const lay = getMedTreatSummaryTableLayout(items.length);
        autoTable(doc, {
            startY: tableStartY,
            head: [[
                isArabic ? 'التاريخ' : 'Date',
                isArabic ? 'المريض' : 'Patient',
                isArabic ? 'النوع' : 'Type',
                isArabic ? 'التفاصيل' : 'Details',
            ]],
            body: items.map((item) => [
                formatMedTreatDate(item),
                item.patientName || 'Unknown Patient',
                item.itemType === 'medication' ? (isArabic ? 'دواء' : 'Medication')
                    : item.itemType === 'surgical' ? (isArabic ? 'جراحي' : 'Surgical')
                        : (isArabic ? 'علاج' : 'Treatment'),
                formatMedTreatEntry(item, isArabic),
            ]),
            theme: 'grid',
            tableWidth: contentWidth,
            headStyles: {
                ...compactHeadStyles(lay.headFont),
                fontSize: lay.headFont,
                cellPadding: lay.cellPadding,
            },
            styles: {
                fontSize: lay.fontSize,
                cellPadding: lay.cellPadding,
                minCellHeight: lay.minCellHeight,
                valign: 'top',
                overflow: 'ellipsize',
            },
            columnStyles: {
                0: { cellWidth: 19 },
                1: { cellWidth: 25 },
                2: { cellWidth: 18 },
                3: { cellWidth: contentWidth - 62 },
            },
            margin: getPdfTableMargins(doc),
        });
        lastTableFinalY = doc.lastAutoTable?.finalY ?? null;
    };

    if (items?.length > 0) {
        const samePatient = allMedTreatItemsSamePatient(items);
        const p0 = samePatient ? items[0] : null;
        const portalPatientId = samePatient && p0
            ? coalescePdfPatientReferenceId(
                p0,
                {
                    name: p0.patientName,
                    patientName: p0.patientName,
                    id: p0.patientId ?? p0.patient_id,
                    patientId: p0.patientId ?? p0.patient_id,
                },
                { patientId: optPatientId, referenceId: optReferenceId },
            )
            : (optPatientId ?? optReferenceId ?? null);

        if (samePatient) {
            tableStartY = (await addPatientInfoBlock(
                doc,
                {
                    name: p0.patientName,
                    patientName: p0.patientName,
                    id: portalPatientId,
                    patientId: portalPatientId,
                },
                PRINT_MARGIN_LEFT,
                printedLineBottomY + 2,
                {
                    isArabic,
                    pageWidth,
                    recordDate: new Date().toISOString().split('T')[0],
                    patientId: portalPatientId,
                    referenceId: portalPatientId,
                    sourceRecord: p0,
                }
            )) + 4;
            const medications = items.filter((i) => i.itemType === 'medication');
            const nonMedications = items.filter((i) => i.itemType !== 'medication');
            let nextY = tableStartY;

            if (medications.length > 0) {
                const layout = getMedicationTableLayout(medications.length, contentWidth);
                const [cw0, cw1, cw2, cw3] = layout.columnWidths;
                const medBody = medications.map((med, idx) => buildMedicationPdfTableRow(med, idx, isArabic));

                autoTable(doc, {
                    startY: nextY,
                    head: [[
                        '#',
                        t('medication'),
                        t('frequency'),
                        t('duration'),
                    ]],
                    body: medBody,
                    theme: 'grid',
                    tableWidth: contentWidth,
                    styles: {
                        fontSize: layout.bodyFont,
                        cellPadding: layout.cellPadding,
                        overflow: 'linebreak',
                        minCellHeight: layout.minCellHeight,
                        valign: 'top',
                    },
                    headStyles: {
                        ...neutralMedicationHeadStyles(layout.headFont),
                        fontSize: layout.headFont,
                        cellPadding: layout.cellPadding,
                    },
                    bodyStyles: {
                        fontSize: layout.bodyFont,
                        cellPadding: layout.cellPadding,
                        overflow: 'linebreak',
                        minCellHeight: layout.minCellHeight,
                        valign: 'top',
                    },
                    columnStyles: {
                        0: { cellWidth: cw0, halign: 'center' },
                        1: { cellWidth: cw1, overflow: 'linebreak' },
                        2: { cellWidth: cw2, overflow: 'linebreak' },
                        3: { cellWidth: cw3, halign: 'center', overflow: 'linebreak' },
                    },
                    margin: getPdfTableMargins(doc),
                });
                lastTableFinalY = doc.lastAutoTable?.finalY ?? null;
                nextY = lastTableFinalY + 6;

                const hasMedNotes = medications.some(isTopicalSteroidMedication)
                    || collectVisitLevelInstructions(medications, options).length > 0;
                if (hasMedNotes) {
                    nextY = appendMedicationPdfNotes(doc, medications, nextY, contentWidth, {
                        dense: medications.length > 6,
                        isArabic,
                        visitInstructions: options.visitInstructions,
                    });
                    lastTableFinalY = nextY;
                    nextY += 4;
                }
            }

            if (nonMedications.length > 0) {
                const lay = getMedTreatSummaryTableLayout(nonMedications.length);
                const dateW = 19;
                const typeW = 18;
                const detailsW = Math.max(20, contentWidth - dateW - typeW);
                autoTable(doc, {
                    startY: nextY,
                    head: [[
                        isArabic ? 'التاريخ' : 'Date',
                        isArabic ? 'النوع' : 'Type',
                        isArabic ? 'التفاصيل' : 'Details',
                    ]],
                    body: nonMedications.map((row) => medTreatOtherSummaryRow(row, isArabic)),
                    theme: 'grid',
                    tableWidth: contentWidth,
                    headStyles: {
                        ...compactHeadStyles(lay.headFont),
                        fontSize: lay.headFont,
                        cellPadding: lay.cellPadding,
                    },
                    styles: {
                        fontSize: lay.fontSize,
                        cellPadding: lay.cellPadding,
                        minCellHeight: lay.minCellHeight,
                        valign: 'top',
                        overflow: 'ellipsize',
                    },
                    columnStyles: {
                        0: { cellWidth: dateW },
                        1: { cellWidth: typeW },
                        2: { cellWidth: detailsW },
                    },
                    margin: getPdfTableMargins(doc),
                });
                lastTableFinalY = doc.lastAutoTable?.finalY ?? null;
            }
        } else {
            runMixedPatientTable();
        }

        await finalizePatientReportPdf(doc, pageWidth, isArabic, portalPatientId, lastTableFinalY ?? pdfPageY(72));
    } else {
        await finalizePatientReportPdf(doc, pageWidth, isArabic, null, pdfPageY(72));
    }

    const safeTitle = title.replace(/[^a-z0-9_-]+/gi, '_').toLowerCase();
    const fileName = `${safeTitle}_${Date.now()}.pdf`;
    return emitPdfResult(doc, fileName, { forPrint, returnDoc });
};

/**
 * Print the current Medications & Treatments section/list as one A5 PDF.
 */
export const printMedicationsTreatmentsPDF = async (items = [], title = 'Medications & Treatments', options = {}) => {
    return generateMedicationsTreatmentsPDF(items, title, { ...options, forPrint: true });
};

export const downloadMedicationsTreatmentsPDF = async (items = [], title = 'Medications & Treatments', options = {}) => {
    return generateMedicationsTreatmentsPDF(items, title, { ...options, forPrint: false });
};

/**
 * Generate a PDF for Lab Results with Arabic support
 */
export const generateLabPDF = async (lab, patientName, options = {}) => {
    const { isArabic = false, forPrint = false, returnDoc = false, patientId = null, referenceId = null } = options;
    const portalId = coalescePdfPatientReferenceId(lab, patientName, { patientId, referenceId });
    const patientForPdf = normalizePatientArgForPdf(patientName, portalId);
    const doc = createPdfDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);

    addHeader(doc, pageWidth, isArabic);

    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    const title = isArabic ? 'تقرير التحاليل المعملية' : 'LABORATORY RESULTS';
    doc.text(title, centerX, pdfPageY(52), { align: 'center', maxWidth: contentWidth });

    // Patient Info
    let currentY = await addPatientInfoBlock(doc, patientForPdf, PRINT_MARGIN_LEFT, pdfPageY(62), {
        isArabic,
        pageWidth,
        recordDate: lab.date || new Date().toISOString().split('T')[0],
        patientId: portalId,
        referenceId: portalId,
        sourceRecord: lab,
    });

    // Panel type
    if (lab.panelType) {
        doc.setFont('helvetica', 'bold');
        doc.text(`${isArabic ? 'نوع التحليل:' : 'Panel Type:'} ${lab.panelType}`, PRINT_MARGIN_LEFT, currentY, { maxWidth: contentWidth });
        currentY += 10;
    }

    // Purpose
    if (lab.purpose) {
        doc.setFont('helvetica', 'bold');
        doc.text(isArabic ? 'الغرض:' : 'Purpose:', PRINT_MARGIN_LEFT, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(lab.purpose, PRINT_MARGIN_LEFT, currentY + 7, { maxWidth: contentWidth });
        currentY += 20;
    }

    // Results
    if (lab.results) {
        doc.setFont('helvetica', 'bold');
        doc.text(isArabic ? 'النتائج:' : 'Results:', PRINT_MARGIN_LEFT, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(lab.results, PRINT_MARGIN_LEFT, currentY + 7, { maxWidth: contentWidth });
        currentY += 25;
    }

    // Fitness status
    if (lab.fitnessStatus) {
        doc.setFont('helvetica', 'bold');
        const statusLabel = isArabic ? 'حالة اللياقة:' : 'Fitness Status:';
        const statusValue = lab.fitnessStatus === 'Fit' 
            ? (isArabic ? 'لائق' : 'Fit')
            : lab.fitnessStatus === 'Unfit'
                ? (isArabic ? 'غير لائق' : 'Unfit')
                : (isArabic ? 'في انتظار النتيجة' : 'Pending');
        doc.text(`${statusLabel} ${statusValue}`, PRINT_MARGIN_LEFT, currentY, { maxWidth: contentWidth });
        currentY += 15;
    }

    // Notes
    if (lab.notes) {
        doc.setFont('helvetica', 'bold');
        doc.text(isArabic ? 'ملاحظات:' : 'Notes:', PRINT_MARGIN_LEFT, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(lab.notes, PRINT_MARGIN_LEFT, currentY + 7, { maxWidth: contentWidth });
        currentY += 20;
    }

    await finalizePatientReportPdf(doc, pageWidth, isArabic, portalId, currentY);

    const fileName = `lab_results_${getPatientFileName(patientForPdf)}_${lab.id}.pdf`;
    return emitPdfResult(doc, fileName, { forPrint, returnDoc });
};

/**
 * Print lab PDF
 */
export const printLabPDF = (lab, patientName, isArabicOrOptions = false) => {
    return generateLabPDF(lab, patientName, { ...mergeArabicOrPdfOptions(isArabicOrOptions), forPrint: true });
};

/**
 * Download lab PDF
 */
export const downloadLabPDF = (lab, patientName, isArabicOrOptions = false) => {
    return generateLabPDF(lab, patientName, { ...mergeArabicOrPdfOptions(isArabicOrOptions), forPrint: false });
};

/**
 * Generate a PDF for Surgery Report with Arabic support
 */
export const generateSurgeryPDF = async (surgery, patientName, options = {}) => {
    const { isArabic = false, forPrint = false, returnDoc = false, patientId = null, referenceId = null } = options;
    const portalId = coalescePdfPatientReferenceId(surgery, patientName, { patientId, referenceId });
    const patientForPdf = normalizePatientArgForPdf(patientName, portalId);
    const doc = createPdfDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);

    addHeader(doc, pageWidth, isArabic);

    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    const title = isArabic ? 'تقرير العملية الجراحية' : 'SURGICAL REPORT';
    doc.text(title, centerX, pdfPageY(52), { align: 'center', maxWidth: contentWidth });

    // Patient Info
    let currentY = await addPatientInfoBlock(doc, patientForPdf, PRINT_MARGIN_LEFT, pdfPageY(62), {
        isArabic,
        pageWidth,
        recordDate: surgery.datePerformed || surgery.dateScheduled || new Date().toISOString().split('T')[0],
        patientId: portalId,
        referenceId: portalId,
        sourceRecord: surgery,
    });

    // Procedure name
    if (surgery.procedureName) {
        doc.setFont('helvetica', 'bold');
        doc.text(`${isArabic ? 'العملية:' : 'Procedure:'} ${surgery.procedureName}`, PRINT_MARGIN_LEFT, currentY, { maxWidth: contentWidth });
        currentY += 10;
    }

    // Eye
    if (surgery.eye) {
        doc.text(`${isArabic ? 'العين:' : 'Eye:'} ${surgery.eye}`, PRINT_MARGIN_LEFT, currentY, { maxWidth: contentWidth });
        currentY += 10;
    }

    // Surgery type
    if (surgery.surgeryType) {
        const typeLabel = surgery.surgeryType === 'minor' 
            ? (isArabic ? 'صغيرة' : 'Minor')
            : (isArabic ? 'كبيرة' : 'Major');
        doc.text(`${isArabic ? 'نوع العملية:' : 'Surgery Type:'} ${typeLabel}`, PRINT_MARGIN_LEFT, currentY, { maxWidth: contentWidth });
        currentY += 10;
    }

    // Pre-op diagnosis
    if (surgery.preOpDiagnosis) {
        doc.setFont('helvetica', 'bold');
        doc.text(isArabic ? 'التشخيص قبل العملية:' : 'Pre-op Diagnosis:', PRINT_MARGIN_LEFT, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(surgery.preOpDiagnosis, PRINT_MARGIN_LEFT, currentY + 7, { maxWidth: contentWidth });
        currentY += 20;
    }

    // IOL details
    if (surgery.iolRequired && surgery.iolPower) {
        doc.setFont('helvetica', 'bold');
        doc.text(isArabic ? 'تفاصيل العدسة:' : 'IOL Details:', PRINT_MARGIN_LEFT, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(`${surgery.iolType || ''} ${surgery.iolModel || ''} ${surgery.iolPower}D`, PRINT_MARGIN_LEFT, currentY + 7, { maxWidth: contentWidth });
        currentY += 20;
    }

    // Complications
    if (surgery.complications && surgery.complications.toLowerCase() !== 'none') {
        doc.setFont('helvetica', 'bold');
        doc.text(isArabic ? 'المضاعفات:' : 'Complications:', PRINT_MARGIN_LEFT, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(surgery.complications, PRINT_MARGIN_LEFT, currentY + 7, { maxWidth: contentWidth });
        currentY += 20;
    }

    // Post-op instructions
    if (surgery.postOpInstructions) {
        doc.setFont('helvetica', 'bold');
        doc.text(isArabic ? 'تعليمات ما بعد العملية:' : 'Post-op Instructions:', PRINT_MARGIN_LEFT, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(surgery.postOpInstructions, PRINT_MARGIN_LEFT, currentY + 7, { maxWidth: contentWidth });
        currentY += 20;
    }

    await finalizePatientReportPdf(doc, pageWidth, isArabic, portalId, currentY);

    const fileName = `surgery_${getPatientFileName(patientForPdf)}_${surgery.id}.pdf`;
    return emitPdfResult(doc, fileName, { forPrint, returnDoc });
};

/**
 * Print surgery PDF
 */
export const printSurgeryPDF = (surgery, patientName, isArabicOrOptions = false) => {
    return generateSurgeryPDF(surgery, patientName, { ...mergeArabicOrPdfOptions(isArabicOrOptions), forPrint: true });
};

/**
 * Download surgery PDF
 */
export const downloadSurgeryPDF = (surgery, patientName, isArabicOrOptions = false) => {
    return generateSurgeryPDF(surgery, patientName, { ...mergeArabicOrPdfOptions(isArabicOrOptions), forPrint: false });
};

/** Compact clinic header for single-page clinical reports (A5). */
const addCompactReportHeader = (doc, pageWidth, isArabic = false) => {
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(isArabic ? CLINIC_INFO.nameAr : CLINIC_INFO.name, centerX, pdfPageY(12), { align: 'center', maxWidth: contentWidth });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(isArabic ? CLINIC_INFO.title1Ar : CLINIC_INFO.title1, centerX, pdfPageY(16.5), { align: 'center', maxWidth: contentWidth });
    doc.text(isArabic ? CLINIC_INFO.title2Ar : CLINIC_INFO.title2, centerX, pdfPageY(19.5), { align: 'center', maxWidth: contentWidth });
    doc.text(isArabic ? CLINIC_INFO.title3Ar : CLINIC_INFO.title3, centerX, pdfPageY(22.5), { align: 'center', maxWidth: contentWidth });
    doc.setDrawColor(0, 128, 128);
    doc.setLineWidth(0.35);
    doc.line(PRINT_MARGIN_LEFT, pdfPageY(25), pageWidth - PRINT_MARGIN_RIGHT, pdfPageY(25));
};

/**
 * Generate a generic report PDF with Arabic support (single A5 page, clinical sections only).
 */
export const generateReportPDF = async (report, patientName, options = {}) => {
    const { isArabic = false, forPrint = false, returnDoc = false, patientId = null, referenceId = null } = options;
    const portalId = coalescePdfPatientReferenceId(report, patientName, { patientId, referenceId });
    const patientForPdf = normalizePatientArgForPdf(patientName, portalId);
    const doc = createPdfDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);
    const footerY = pageHeight - 10 + PDF_PAGE_TOP_MARGIN_MM + PDF_FOOTER_EXTRA_OFFSET_MM;
    const maxBodyY = footerY - 6;

    addCompactReportHeader(doc, pageWidth, isArabic);

    const rawTitle = report.title || report.type || 'Medical Report';
    const localizedTitle = isArabic
        ? translateReportDocumentTitle(rawTitle, 'ar')
        : rawTitle;
    const reportTitle = localizedTitle.toUpperCase();
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(reportTitle, centerX, pdfPageY(29), { align: 'center', maxWidth: contentWidth });

    const dateLabel = isArabic ? 'التاريخ:' : 'Date:';
    const idLabel = isArabic ? 'رقم المريض:' : 'Patient ID:';
    const reportDate = report.date || new Date().toISOString().split('T')[0];
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'normal');
    const metaLine = [
        `${isArabic ? 'المريض:' : 'Patient:'} ${getPatientName(patientForPdf)}`,
        portalId ? `${idLabel} ${portalId}` : '',
        `${dateLabel} ${reportDate}`,
    ]
        .filter(Boolean)
        .join('   ·   ');
    doc.text(metaLine, PRINT_MARGIN_LEFT, pdfPageY(34), { maxWidth: contentWidth });

    let currentY = pdfPageY(38);
    let bodyFontSize = 10;
    let lineStep = 4.2;
    let sectionTitleSize = 10.5;
    let sectionGap = 1.8;

    const pdfBlocks = report.pdfBlocks?.length ? report.pdfBlocks : null;
    const reportContent =
        report.content ||
        (report.sections
            ? Object.entries(report.sections)
                  .filter(([, value]) => String(value || '').trim())
                  .map(([section, value]) => `${section}\n${value}`)
                  .join('\n\n')
            : '');
    const bodyText = reportContent || report.summary || '';

    const estimateHeight = (blocks) => {
        let h = 0;
        const measure = (blocksToMeasure, fontSize, step) => {
            doc.setFontSize(fontSize);
            blocksToMeasure.forEach((block) => {
                if (block.title) h += sectionTitleSize * 0.35 + sectionGap;
                const lines = doc.splitTextToSize(String(block.body || ''), contentWidth);
                h += lines.length * step + sectionGap;
            });
        };
        if (blocks?.length) measure(blocks, bodyFontSize, lineStep);
        else if (bodyText) {
            const lines = doc.splitTextToSize(bodyText, contentWidth);
            h += lines.length * lineStep;
        }
        if (report.signature?.trim()) h += 12;
        return h;
    };

    const blocksForLayout =
        pdfBlocks?.length ? pdfBlocks : bodyText ? [{ title: null, body: bodyText }] : [];
    while (currentY + estimateHeight(blocksForLayout) > maxBodyY && bodyFontSize > 8.5) {
        bodyFontSize -= 0.5;
        lineStep -= 0.25;
        sectionTitleSize -= 0.3;
        sectionGap -= 0.15;
    }

    const renderBlock = (blockTitle, blockBody) => {
        if (!String(blockBody || '').trim()) return;
        if (blockTitle) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(sectionTitleSize);
            doc.text(blockTitle, PRINT_MARGIN_LEFT, currentY);
            currentY += sectionTitleSize * 0.35 + sectionGap * 0.5;
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(bodyFontSize);
        doc.splitTextToSize(String(blockBody), contentWidth).forEach((line) => {
            if (currentY > maxBodyY) return;
            doc.text(line, PRINT_MARGIN_LEFT, currentY);
            currentY += lineStep;
        });
        currentY += sectionGap;
    };

    if (pdfBlocks?.length) {
        pdfBlocks.forEach((block) => renderBlock(block.title || null, block.body));
    } else if (bodyText) {
        renderBlock(null, bodyText);
    } else if (report.doctorNotes?.trim()) {
        renderBlock(isArabic ? 'ملاحظات إضافية' : 'Additional Notes', report.doctorNotes);
    }

    if (
        currentY < maxBodyY - 18 &&
        isReportSectionIncluded(REPORT_SECTION_INCLUSION_KEYS.SIGNATURE, report?.sectionInclusion)
    ) {
        currentY = await addPhysicianSignatureToPdf(doc, pageWidth, currentY + 2, {
            isArabic,
            align: 'right',
            marginRight: PRINT_MARGIN_RIGHT,
            maxY: maxBodyY,
        });
    }

    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(`Tel: ${CLINIC_INFO.phone}`, centerX, footerY - 4, { align: 'center', maxWidth: contentWidth });
    doc.text(
        isArabic ? CLINIC_INFO.addressAr : CLINIC_INFO.address,
        centerX,
        footerY,
        { align: 'center', maxWidth: contentWidth }
    );
    doc.setTextColor(0);

    const fileName = `report_${getPatientFileName(patientForPdf)}_${report.id || Date.now()}.pdf`;
    return emitPdfResult(doc, fileName, { forPrint, returnDoc });
};

export const printReportPDF = async (report, patientName, isArabicOrOptions = false) => {
    const opts = mergeArabicOrPdfOptions(isArabicOrOptions);
    const { reportNeedsHtmlExport, printReportHtml } = await import('./reportPdfExport');
    if (reportNeedsHtmlExport(report, opts.isArabic)) {
        return printReportHtml(report, patientName, opts);
    }
    return generateReportPDF(report, patientName, { ...opts, forPrint: true });
};

export const downloadReportPDF = async (report, patientName, isArabicOrOptions = false) => {
    const opts = mergeArabicOrPdfOptions(isArabicOrOptions);
    const { reportNeedsHtmlExport, downloadReportHtml } = await import('./reportPdfExport');
    if (reportNeedsHtmlExport(report, opts.isArabic)) {
        return downloadReportHtml(report, patientName, opts);
    }
    return generateReportPDF(report, patientName, { ...opts, forPrint: false });
};

const formatRecordValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') {
        return Object.entries(value)
            .map(([key, nestedValue]) => `${key}: ${formatRecordValue(nestedValue)}`)
            .join(' | ');
    }
    return String(value);
};

const humanizeKey = (key) => key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

const cleanRecordEntries = (record) => Object.entries(record || {})
    .filter(([key]) => ![
        'id',
        'patientId',
        'patient_id',
        'patientID',
        'patientName',
        'referenceId',
        'dob',
        'dateOfBirth',
        'birthDate',
        'age',
        'contact',
        'phone',
        'phoneNumber',
        'mobile',
        'mobileNumber',
        '_dirty',
        '_deleted',
        '_lastLocalChangeAt'
    ].includes(key))
    .map(([key, value]) => [humanizeKey(key), formatRecordValue(value)]);

export const generateGenericRecordPDF = async (record, options = {}) => {
    const {
        title = 'CLINIC RECORD',
        subtitle = '',
        patientName = record?.patientName || '',
        forPrint = false,
        returnDoc = false,
        filePrefix = 'clinic_record',
        patientId = record?.patientId || null,
        referenceId = null,
        isArabic = false,
        recordDate: recordDateOption = null,
    } = options;
    const doc = createPdfDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);

    addHeader(doc, pageWidth, isArabic);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(title.toUpperCase(), centerX, pdfPageY(52), { align: 'center', maxWidth: contentWidth });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    let yAfterTitle = pdfPageY(60);
    if (subtitle) {
        doc.text(subtitle, centerX, yAfterTitle, { align: 'center', maxWidth: contentWidth });
        yAfterTitle += 8;
    }

    const resolvedPatientId = coalescePdfPatientReferenceId(record, patientName, {
        patientId: patientId != null && String(patientId).trim() !== '' ? patientId : null,
        referenceId: referenceId != null && String(referenceId).trim() !== '' ? referenceId : null,
    });
    const nameStr = typeof patientName === 'string' ? patientName : (patientName?.name || patientName?.patientName || '');
    const hasPatientContext = Boolean((nameStr && String(nameStr).trim()) || resolvedPatientId);
    const patientForBlock = normalizePatientArgForPdf(patientName, resolvedPatientId);
    const recordDate =
        recordDateOption
        || record?.date
        || (typeof record?.createdAt === 'string' ? record.createdAt.split('T')[0] : '')
        || record?.dateScheduled
        || new Date().toISOString().split('T')[0];

    let tableStartY;
    if (hasPatientContext) {
        tableStartY = await addPatientInfoBlock(doc, patientForBlock, PRINT_MARGIN_LEFT, yAfterTitle + 2, {
            isArabic,
            pageWidth,
            recordDate,
            patientId: resolvedPatientId,
            referenceId: resolvedPatientId,
            sourceRecord: record,
        }) + 4;
    } else {
        tableStartY = yAfterTitle + 4;
    }

    doc.text(`Printed: ${new Date().toLocaleString()}`, PRINT_MARGIN_LEFT, tableStartY, { maxWidth: contentWidth });
    tableStartY += 8;

    autoTable(doc, {
        startY: tableStartY,
        head: [['Field', 'Value']],
        body: cleanRecordEntries(record),
        theme: 'grid',
        headStyles: compactHeadStyles(8),
        styles: { ...compactTableStyles(8), valign: 'top' },
        columnStyles: {
            0: { cellWidth: 35, fontStyle: 'bold' },
            1: { cellWidth: contentWidth - 35 },
        },
        margin: getPdfTableMargins(doc),
    });

    await finalizePatientReportPdf(
        doc,
        pageWidth,
        isArabic,
        resolvedPatientId,
        doc.lastAutoTable?.finalY ?? tableStartY
    );

    const safeTitle = filePrefix.replace(/[^a-z0-9_-]+/gi, '_').toLowerCase();
    const fileName = `${safeTitle}_${record?.id || Date.now()}.pdf`;
    return emitPdfResult(doc, fileName, { forPrint, returnDoc });
};

export const printGenericRecordPDF = (record, options = {}) => {
    return generateGenericRecordPDF(record, { ...options, forPrint: true });
};

export const downloadGenericRecordPDF = (record, options = {}) => {
    return generateGenericRecordPDF(record, { ...options, forPrint: false });
};

const SERVICE_INVOICE_PDF_OPTIONS = {
    title: 'Service Invoice',
    subtitle: 'Clinic billing',
    filePrefix: 'service_invoice',
};

export const printServiceInvoicePDF = (invoice) =>
    printGenericRecordPDF(invoice, {
        ...SERVICE_INVOICE_PDF_OPTIONS,
        patientName: invoice?.patientName || '',
        patientId: invoice?.patientId ?? null,
    });

export const downloadServiceInvoicePDF = (invoice) =>
    downloadGenericRecordPDF(invoice, {
        ...SERVICE_INVOICE_PDF_OPTIONS,
        patientName: invoice?.patientName || '',
        patientId: invoice?.patientId ?? null,
    });

export const printAppointmentPDF = (appointment, patientName) => {
    return printGenericRecordPDF(appointment, {
        title: 'Appointment Slip',
        subtitle: 'BookingApp / ClinicsReceptionApp',
        patientName,
        patientId: appointment?.patientId || null,
        filePrefix: 'appointment',
    });
};

export const downloadAppointmentPDF = (appointment, patientName) => {
    return downloadGenericRecordPDF(appointment, {
        title: 'Appointment Slip',
        subtitle: 'BookingApp / ClinicsReceptionApp',
        patientName,
        patientId: appointment?.patientId || null,
        filePrefix: 'appointment',
    });
};

export const printPatientSummaryPDF = (patient) => {
    return printGenericRecordPDF(patient, {
        title: 'Patient Summary',
        subtitle: 'PatientsManagementLib',
        patientName: patient?.name,
        patientId: patient?.id || null,
        filePrefix: 'patient_summary',
    });
};

export const downloadPatientSummaryPDF = (patient) => {
    return downloadGenericRecordPDF(patient, {
        title: 'Patient Summary',
        subtitle: 'PatientsManagementLib',
        patientName: patient?.name,
        patientId: patient?.id || null,
        filePrefix: 'patient_summary',
    });
};

export const printQueuePDF = async (queueItems = [], title = 'Patient Queue') => {
    const doc = createPdfDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);
    addHeader(doc, pageWidth);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(title.toUpperCase(), centerX, pdfPageY(52), { align: 'center', maxWidth: contentWidth });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Printed: ${new Date().toLocaleString()}`, PRINT_MARGIN_LEFT, pdfPageY(62));

    autoTable(doc, {
        startY: pdfPageY(72),
        head: [['#', 'Patient', 'Time', 'Status', 'Reason']],
        body: queueItems.map((item, index) => [
            index + 1,
            item.patientName || '-',
            item.arrivalTime || item.time || '-',
            item.status || '-',
            item.reason || '-',
        ]),
        theme: 'grid',
        headStyles: compactHeadStyles(8),
        styles: compactTableStyles(8),
        margin: getPdfTableMargins(doc),
    });

    await finalizePatientReportPdf(doc, pageWidth, false, null, doc.lastAutoTable?.finalY ?? pdfPageY(72));
    const safeTitle = title.replace(/[^a-z0-9_-]+/gi, '_').toLowerCase();
    return printPdfDoc(doc, `${safeTitle}_${Date.now()}.pdf`);
};

export const printOperationsModuleCatalogPDF = async (modules = []) => {
    const doc = createPdfDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = getContentCenterX(pageWidth);
    const contentWidth = getContentWidth(pageWidth);
    addHeader(doc, pageWidth);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('CLINIC OPERATIONS MODULE CATALOG', centerX, pdfPageY(52), { align: 'center', maxWidth: contentWidth });

    autoTable(doc, {
        startY: pdfPageY(64),
        head: [['Module', 'Description', 'Capabilities']],
        body: modules.map((module) => [
            module.title,
            module.description || '',
            module.features.join(', '),
        ]),
        theme: 'striped',
        headStyles: compactHeadStyles(7),
        styles: { ...compactTableStyles(7), valign: 'top' },
        columnStyles: {
            0: { cellWidth: 28, fontStyle: 'bold' },
            1: { cellWidth: 38 },
            2: { cellWidth: contentWidth - 66 },
        },
        margin: getPdfTableMargins(doc),
    });

    await finalizePatientReportPdf(doc, pageWidth, false, null, doc.lastAutoTable?.finalY ?? pdfPageY(64));
    return printPdfDoc(doc, `operations_module_catalog_${Date.now()}.pdf`);
};

export const printComrecModuleCatalogPDF = printOperationsModuleCatalogPDF;
